import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Send, Clock, User } from 'lucide-react';

const PRIORITIES = { baja: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20', media: 'bg-amber-500/10 text-amber-500 border-amber-500/20', alta: 'bg-red-500/10 text-red-400 border-red-500/20' };
const STATUS_LABELS = { pendiente: 'Pendiente', en_progreso: 'En Progreso', hecho: 'Hecho' };

export default function TicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await API.get(`/tickets/${id}`);
      setTicket(res.data);
    } catch { toast.error('Error cargando ticket'); navigate('/tickets'); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (newStatus) => {
    try {
      await API.put(`/tickets/${id}`, { status: newStatus });
      toast.success('Estado actualizado');
      load();
    } catch { toast.error('Error'); }
  };

  const handleComment = async () => {
    if (!comment.trim()) return;
    setSending(true);
    try {
      await API.post(`/tickets/${id}/comments`, { text: comment });
      setComment('');
      load();
    } catch { toast.error('Error enviando comentario'); }
    finally { setSending(false); }
  };

  if (!ticket) return <div className="flex items-center justify-center py-20"><div className="text-amber-500 animate-pulse">Cargando...</div></div>;

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl" data-testid="ticket-detail-page">
      <Button variant="ghost" onClick={() => navigate('/tickets')} className="text-zinc-400 hover:text-zinc-200 -ml-2" data-testid="back-to-tickets">
        <ArrowLeft className="w-4 h-4 mr-2" /> Volver a Tickets
      </Button>

      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-zinc-50">{ticket.title}</h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="outline" className={`text-xs ${PRIORITIES[ticket.priority]}`}>{ticket.priority}</Badge>
            <span className="text-xs text-zinc-500">{ticket.date}</span>
            {ticket.start_time && <span className="text-xs text-zinc-600">{ticket.start_time} - {ticket.end_time}</span>}
          </div>
        </div>
        {hasPermission('tickets.update') && (
          <Select value={ticket.status} onValueChange={handleStatusChange}>
            <SelectTrigger className="bg-zinc-950/50 border-white/10 w-40" data-testid="ticket-status-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-zinc-950 border-white/10">
              <SelectItem value="pendiente">Pendiente</SelectItem>
              <SelectItem value="en_progreso">En Progreso</SelectItem>
              <SelectItem value="hecho">Hecho</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      <Card className="glass-card">
        <CardContent className="pt-6 space-y-4">
          {ticket.description && (
            <div>
              <Label className="text-xs text-zinc-500 uppercase tracking-wider">Descripcion</Label>
              <p className="text-sm text-zinc-300 mt-1 whitespace-pre-wrap">{ticket.description}</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-zinc-500 uppercase tracking-wider">Creado por</Label>
              <p className="text-sm text-zinc-300 mt-1 flex items-center gap-1"><User className="w-3 h-3" />{ticket.created_by_name}</p>
            </div>
            <div>
              <Label className="text-xs text-zinc-500 uppercase tracking-wider">Estado</Label>
              <p className="text-sm text-zinc-300 mt-1">{STATUS_LABELS[ticket.status]}</p>
            </div>
          </div>
          {ticket.assigned_names?.length > 0 && (
            <div>
              <Label className="text-xs text-zinc-500 uppercase tracking-wider">Asignados</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {ticket.assigned_names.map((name, i) => (
                  <Badge key={i} variant="outline" className="text-xs text-zinc-300 border-white/10">{name}</Badge>
                ))}
              </div>
            </div>
          )}
          {ticket.tags?.length > 0 && (
            <div>
              <Label className="text-xs text-zinc-500 uppercase tracking-wider">Etiquetas</Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {ticket.tags.map(tag => (
                  <span key={tag} className="text-xs px-2 py-0.5 rounded bg-zinc-800 text-zinc-400">{tag}</span>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Comments */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="text-base text-zinc-200">Comentarios ({ticket.comments?.length || 0})</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {ticket.comments?.length === 0 && (
            <p className="text-sm text-zinc-600 text-center py-4">Sin comentarios aun</p>
          )}
          {ticket.comments?.map(c => (
            <div key={c.id} className="flex gap-3" data-testid={`comment-${c.id}`}>
              <div className="w-7 h-7 rounded-full bg-amber-500/20 flex items-center justify-center text-xs text-amber-500 font-medium shrink-0">
                {(c.user_name || '?').charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-sm font-medium text-zinc-200">{c.user_name}</span>
                  <span className="text-xs text-zinc-600">{new Date(c.created_at).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })}</span>
                </div>
                <p className="text-sm text-zinc-400">{c.text}</p>
              </div>
            </div>
          ))}

          <div className="flex gap-2 pt-2 border-t border-white/5">
            <Input
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder="Escribe un comentario..."
              className="bg-zinc-950/50 border-white/10 flex-1"
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleComment()}
              data-testid="comment-input"
              aria-label="Comentario"
            />
            <Button onClick={handleComment} disabled={sending || !comment.trim()}
              className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="send-comment-button">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
